import { useTranslation } from '../i18n';

interface IOSPWAGuideProps {
  isVisible: boolean;
  onDismiss: () => void;
}

export function IOSPWAGuide({ isVisible, onDismiss }: IOSPWAGuideProps) {
  const { t } = useTranslation();

  if (!isVisible) return null;

  return (
    <div style={styles.overlay}>
      <div style={styles.container}>
        {/* Close button */}
        <button onClick={onDismiss} style={styles.closeBtn} aria-label="Close">
          ✕
        </button>

        {/* Header */}
        <div style={styles.header}>
          <span style={styles.icon}>📲</span>
          <h2 style={styles.title}>{t('install_app')}</h2>
        </div>

        {/* Instructions */}
        <div style={styles.content}>
          <p style={styles.subtitle}>{t('add_to_home_screen_ios')}</p>

          <div style={styles.steps}>
            <div style={styles.step}>
              <div style={styles.stepNumber}>1</div>
              <div>
                <p style={styles.stepTitle}>{t('tap_share')}</p>
                <p style={styles.stepDesc}>
                  {t('tap_share_button_at_bottom')}
                </p>
              </div>
            </div>

            <div style={styles.step}>
              <div style={styles.stepNumber}>2</div>
              <div>
                <p style={styles.stepTitle}>{t('select_add_home_screen')}</p>
                <p style={styles.stepDesc}>
                  {t('scroll_and_find_add_home_screen')}
                </p>
              </div>
            </div>

            <div style={styles.step}>
              <div style={styles.stepNumber}>3</div>
              <div>
                <p style={styles.stepTitle}>{t('confirm')}</p>
                <p style={styles.stepDesc}>{t('tap_add_to_confirm')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div style={styles.footer}>
          <button onClick={onDismiss} style={styles.dismissBtn}>
            {t('got_it')}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '1rem',
  },
  container: {
    position: 'relative',
    backgroundColor: '#ffffff',
    borderRadius: '20px',
    padding: '2rem',
    maxWidth: '420px',
    width: '100%',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    animation: 'slideUp 0.3s ease-out',
  },
  closeBtn: {
    position: 'absolute',
    top: '1rem',
    right: '1rem',
    background: 'none',
    border: 'none',
    fontSize: '1.5rem',
    color: 'var(--on-surface-2)',
    cursor: 'pointer',
    padding: '0.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    marginBottom: '1.5rem',
  },
  icon: {
    fontSize: '2.5rem',
  },
  title: {
    margin: 0,
    fontSize: '1.5rem',
    fontWeight: 700,
    color: 'var(--on-surface)',
    fontFamily: 'var(--manrope)',
  },
  content: {
    marginBottom: '1.5rem',
  },
  subtitle: {
    margin: '0 0 1.5rem 0',
    color: 'var(--on-surface-2)',
    fontSize: '0.95rem',
    lineHeight: 1.5,
  },
  steps: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  step: {
    display: 'flex',
    gap: '1rem',
  },
  stepNumber: {
    minWidth: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: 'var(--primary-container)',
    color: 'var(--primary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: '1rem',
    flexShrink: 0,
  },
  stepTitle: {
    margin: '0 0 0.25rem 0',
    fontWeight: 600,
    color: 'var(--on-surface)',
    fontSize: '0.95rem',
  },
  stepDesc: {
    margin: 0,
    color: 'var(--on-surface-2)',
    fontSize: '0.85rem',
    lineHeight: 1.5,
  },
  footer: {
    display: 'flex',
    gap: '1rem',
  },
  dismissBtn: {
    flex: 1,
    height: '48px',
    borderRadius: '12px',
    border: 'none',
    background: 'linear-gradient(135deg, #005bc0, #004fa9)',
    color: '#ffffff',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'var(--inter)',
  },
};
